import type {
  CostLimitsListResponse,
  GetAdminAccountUsageResponse,
  GetAdminSandboxLimitsResponse,
  GetAdminTaskDetailResponse,
  GetAdminUsageResponse,
  GetAdminUserUsageResponse,
  ListAdminTasksResponse,
  ListAdminTaskVersionsResponse,
  ListAdminUsersResponse,
  UpdateAdminSandboxLimitsRequest,
  UserCostLimitDetailResponse,
} from '@kyumo/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteDefaultCostLimit,
  deleteSandboxLimits,
  deleteSystemCostLimit,
  deleteUserCostLimit,
  fetchAccountUsageData,
  fetchCostLimits,
  fetchSandboxLimits,
  fetchTaskDetail,
  fetchTasks,
  fetchTaskVersion,
  fetchTaskVersions,
  fetchUsageData,
  fetchUserCostLimitDetail,
  fetchUsers,
  fetchUserUsageData,
  resetUserMfa,
  setDefaultCostLimit,
  setSandboxLimits,
  setSystemCostLimit,
  setUserCostLimit,
} from '../services/admin-api';

/**
 * 使用量データを取得するフック
 */
export function useUsageData(month?: string, model?: string) {
  return useQuery<GetAdminUsageResponse>({
    queryKey: ['admin', 'usage', month, model],
    queryFn: () => fetchUsageData(month, model),
  });
}

/**
 * アカウント別の月間利用額を全件取得するフック
 *
 * `useUsageData` の `ranking` は上位 10 件に切られているため、
 * 利用者ごとの金額を引く用途にはこちらを使う
 */
export function useAccountUsageData(month: string) {
  return useQuery<GetAdminAccountUsageResponse>({
    queryKey: ['admin', 'usage', 'users', month],
    queryFn: () => fetchAccountUsageData(month),
  });
}

/**
 * ユーザー別使用量データを取得するフック
 */
export function useUserUsageData(email: string, month?: string, model?: string) {
  return useQuery<GetAdminUserUsageResponse>({
    queryKey: ['admin', 'usage', 'user', email, month, model],
    queryFn: () => fetchUserUsageData(email, month, model),
    enabled: !!email,
  });
}

/**
 * ユーザー一覧を取得するフック（全件取得）
 */
export function useUsers() {
  return useQuery<ListAdminUsersResponse>({
    queryKey: ['admin', 'users'],
    queryFn: () => fetchUsers(),
  });
}

/**
 * タスク一覧を取得するフック（全件）
 */
export function useTasks(email?: string) {
  return useQuery<ListAdminTasksResponse>({
    queryKey: ['admin', 'tasks', email],
    queryFn: () => fetchTasks(email),
  });
}

/**
 * タスク詳細を取得するフック
 */
export function useTaskDetail(email: string, taskId: string) {
  return useQuery<GetAdminTaskDetailResponse>({
    queryKey: ['admin', 'task', email, taskId],
    queryFn: () => fetchTaskDetail(email, taskId),
    enabled: !!email && !!taskId,
  });
}

/**
 * タスクバージョン一覧を取得するフック
 */
export function useTaskVersions(email: string, taskId: string) {
  return useQuery<ListAdminTaskVersionsResponse>({
    queryKey: ['admin', 'task', email, taskId, 'versions'],
    queryFn: () => fetchTaskVersions(email, taskId),
    enabled: !!email && !!taskId,
  });
}

/**
 * 特定バージョンのタスク詳細を取得するフック
 */
export function useTaskVersion(email: string, taskId: string, version: number | undefined) {
  return useQuery<GetAdminTaskDetailResponse>({
    queryKey: ['admin', 'task', email, taskId, 'version', version],
    queryFn: () => fetchTaskVersion(email, taskId, version as number),
    enabled: !!email && !!taskId && version !== undefined,
  });
}

// ==================== コスト上限フック ====================

/**
 * コスト上限一覧を取得するフック
 */
export function useCostLimits() {
  return useQuery<CostLimitsListResponse>({
    queryKey: ['admin', 'cost-limits'],
    queryFn: () => fetchCostLimits(),
  });
}

/**
 * サンドボックス上限（稼働時間・転送量）を取得するフック
 */
export function useSandboxLimits() {
  return useQuery<GetAdminSandboxLimitsResponse>({
    queryKey: ['admin', 'sandbox-limits'],
    queryFn: () => fetchSandboxLimits(),
  });
}

/**
 * サンドボックス上限を読んでいるキャッシュをまとめて捨てる。
 *
 * **上限は 1 つのクエリに閉じていない。** コスト上限（`['admin','cost-limits']`）は
 * 読む側が全員その鍵を見るが、サンドボックス上限は**他の応答へ埋め込まれて**返る。
 *
 * - `['admin','usage', …]` の `sandboxUsage.limits`（使用量カードの分母）
 * - `['admin','users']` の各行の `sandboxUsage.limits`（利用者ごとの割合）
 *
 * 上限の鍵だけを捨てると、`DashboardPage` では**上限カードのすぐ上にある
 * 使用量カードが古い分母のまま**残る。上限を半分にしても割合は動かず、
 * 警告も出ない。変更した本人が、その画面では結果を確かめられない
 */
function invalidateSandboxLimitReaders(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ['admin', 'sandbox-limits'] });
  queryClient.invalidateQueries({ queryKey: ['admin', 'usage'] });
  queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
}

/**
 * サンドボックス上限を設定するミューテーション
 */
export function useSetSandboxLimits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limits: UpdateAdminSandboxLimitsRequest) => setSandboxLimits(limits),
    onSuccess: () => invalidateSandboxLimitReaders(queryClient),
  });
}

/**
 * サンドボックス上限を既定値へ戻すミューテーション
 */
export function useDeleteSandboxLimits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteSandboxLimits(),
    onSuccess: () => invalidateSandboxLimitReaders(queryClient),
  });
}

/**
 * ユーザー個別コスト上限詳細を取得するフック
 */
export function useUserCostLimitDetail(email: string) {
  return useQuery<UserCostLimitDetailResponse>({
    queryKey: ['admin', 'cost-limits', 'user', email],
    queryFn: () => fetchUserCostLimitDetail(email),
    enabled: !!email,
  });
}

/**
 * デフォルトコスト上限を設定するミューテーション
 */
export function useSetDefaultCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limitUSD: number) => setDefaultCostLimit(limitUSD),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * デフォルトコスト上限を削除するミューテーション
 */
export function useDeleteDefaultCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteDefaultCostLimit(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * システム全体コスト上限を設定するミューテーション
 *
 * 成功時にコスト上限一覧のキャッシュを無効化し、既存カードと同時に最新化する。
 */
export function useSetSystemCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limitUSD: number) => setSystemCostLimit(limitUSD),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * システム全体コスト上限を削除するミューテーション
 */
export function useDeleteSystemCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteSystemCostLimit(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * ユーザー個別コスト上限を設定するミューテーション
 */
export function useSetUserCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, limitUSD }: { email: string; limitUSD: number }) =>
      setUserCostLimit(email, limitUSD),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * ユーザー個別コスト上限を削除するミューテーション
 */
export function useDeleteUserCostLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => deleteUserCostLimit(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cost-limits'] });
    },
  });
}

/**
 * 利用者の MFA をリセットするミューテーション
 */
export function useResetUserMfa() {
  return useMutation({
    mutationFn: (email: string) => resetUserMfa(email),
  });
}
